import { ArrowForward } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
    Box,
    ButtonBase,
    Grid,
    Grow,
    Paper,
    Typography,
    useTheme,
} from '@mui/material';
import React, { useEffect } from 'react';
import { ActionType, Toast, ToastType } from '../../app/Slices/SnackBarSlice';
import { CustomIconButton } from '../../layout/styledcomponents/CustomIconButton';
import toast from './Toast';

const IndividualToast = ({ snackbar }: { snackbar: Toast }) => {
    const theme = useTheme();

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        if (
            snackbar.options?.duration !== null &&
            snackbar.options?.duration !== Infinity
        ) {
            timer = setTimeout(() => {
                toast.remove(snackbar.id);
            }, snackbar.options.duration);
        }
        return () => clearTimeout(timer);
    }, [snackbar.id, snackbar.options?.duration]);

    useEffect(() => {
        if (snackbar.bump) {
            const timer = setTimeout(() => {
                toast.update(snackbar.id, { bump: false });
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [snackbar.bump, snackbar.id]);

    const getToastStyles = (type: ToastType) => {
        switch (type) {
            case ToastType.SUCCESS:
                return {
                    backgroundColor: theme.palette.neutral.main,
                    color: theme.palette.text.primary,
                    icon: (
                        <CheckCircleOutlineIcon
                            sx={{
                                mr: 1,
                                fontSize: { xs: 18, sm: 20 },
                                color: theme.palette.success.main,
                            }}
                        />
                    ),
                };
            case ToastType.ERROR:
                return {
                    backgroundColor: theme.palette.neutral.main,
                    color: theme.palette.red.main,
                    icon: (
                        <ErrorOutlineIcon
                            sx={{
                                mr: 1,
                                fontSize: { xs: 18, sm: 20 },
                                color: theme.palette.red.main,
                            }}
                        />
                    ),
                };
            case ToastType.INFO:
                return {
                    backgroundColor: theme.palette.faded.main,
                    color: '#014361',
                    icon: (
                        <ErrorOutlineIcon
                            sx={{
                                mr: 1,
                                fontSize: { xs: 18, sm: 20 },
                                color: '#0288d1',
                            }}
                        />
                    ),
                };
            case ToastType.WARNING:
                return {
                    backgroundColor: theme.palette.warning.contrastText,
                    color: theme.palette.warning.main,
                    icon: (
                        <WarningAmberIcon
                            sx={{
                                mr: 1,
                                fontSize: { xs: 18, sm: 20 },
                                color: theme.palette.warning.main,
                            }}
                        />
                    ),
                };
            default:
                return {
                    backgroundColor: theme.palette.neutral.main,
                    color: theme.palette.text.primary,
                    icon: null,
                };
        }
    };

    const { backgroundColor, color, icon } = getToastStyles(snackbar.type);

    return (
        <Grid sx={{ pb: 1, width: 'auto' }}>
            <Grow
                in={snackbar.show}
                style={{ transformOrigin: 'right' }}
                timeout={700}
            >
                <Paper
                    role='alert'
                    aria-live='polite'
                    elevation={4}
                    sx={{
                        backgroundColor,
                        color,
                        p: { xs: 1, sm: 1.5 },
                        pr: {
                            xs: snackbar.options?.closeButton
                                ? snackbar.options?.dismissLabel
                                    ? 8
                                    : 5
                                : 1,
                            sm: 1.5,
                        },
                        borderRadius: 1.5,
                        width: '100%',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'center', sm: 'center' },
                        gap: { xs: 0, sm: 1.5 },
                        position: 'relative',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            maxWidth: 'auto',
                            textOverflow: 'ellipsis',
                            flex: 1,
                        }}
                    >
                        {icon}
                        <Typography
                            variant='body3'
                            sx={{
                                fontSize: { xs: 13, sm: 14 },
                                pl: 1,
                                lineHeight: 1.4,
                                flex: 1,
                                minWidth: 0,
                            }}
                        >
                            {snackbar.message}
                        </Typography>
                    </Box>

                    {snackbar.options?.actionText && (
                        <Box
                            sx={{
                                flex: { xs: '0 0 100%', sm: '0 1 auto' },
                                width: { xs: '100%', sm: 'auto' },
                                minWidth: 0,
                                pointerEvents: 'auto',
                                position: 'relative',
                                zIndex: 2,
                            }}
                        >
                            <ButtonBase
                                component='button'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const fn = snackbar.options?.actionOnclick;
                                    if (typeof fn === 'function') {
                                        fn();
                                    } else {
                                        console.warn(
                                            'actionOnclick is not a function:',
                                            fn,
                                        );
                                    }
                                }}
                                disableRipple
                                sx={{
                                    width: '100%',
                                    textAlign: { xs: 'left', sm: 'left' },
                                    justifyContent: {
                                        xs: 'flex-start',
                                        sm: 'flex-end',
                                    },
                                    borderRadius: 1,
                                    py: { xs: 0.5, sm: 0 },
                                    px: { xs: 0, sm: 0.5 },
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                    },
                                    position: 'relative',
                                    zIndex: 2,
                                }}
                            >
                                <Typography
                                    variant='body3'
                                    sx={{
                                        fontSize: { xs: 13, sm: 14 },
                                        textDecoration: 'underline',
                                        whiteSpace: 'normal',
                                        textOverflow: 'wrap',
                                        overflowWrap: 'anywhere',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.3,
                                        width: '100%',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {snackbar.options.actionText}
                                    {snackbar.options.actionType ===
                                        ActionType.NAVIGATE && (
                                        <ArrowForward
                                            sx={{
                                                fontSize: { xs: 16, sm: 16 },
                                                pl: 0.5,
                                            }}
                                        />
                                    )}
                                </Typography>
                            </ButtonBase>
                        </Box>
                    )}

                    {snackbar.options?.closeButton &&
                        (snackbar.options.dismissLabel ? (
                            <ButtonBase
                                component='button'
                                aria-label={snackbar.options.dismissLabel}
                                onClick={() => {
                                    const onClose = snackbar.options?.onClose;
                                    if (typeof onClose === 'function') {
                                        onClose();
                                    }
                                    toast.remove(snackbar.id);
                                }}
                                disableRipple
                                sx={{
                                    position: {
                                        xs: 'absolute',
                                        sm: 'static',
                                    },
                                    top: { xs: 4, sm: 'auto' },
                                    right: { xs: 4, sm: 'auto' },
                                    alignSelf: { xs: 'flex-start' },
                                    ml: { xs: 0, sm: 0.5 },
                                    flexShrink: 0,
                                    py: { xs: 0.5, sm: 0 },
                                    px: { xs: 0.5, sm: 0.5 },
                                    borderRadius: 1,
                                    zIndex: 1,
                                    pointerEvents: 'auto',
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                <Typography
                                    variant='body3'
                                    sx={{
                                        fontSize: { xs: 13, sm: 14 },
                                        textDecoration: 'underline',
                                        whiteSpace: 'nowrap',
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {snackbar.options.dismissLabel}
                                </Typography>
                            </ButtonBase>
                        ) : (
                            <CustomIconButton
                                aria-label='Close'
                                size='small'
                                color='inherit'
                                onClick={() => {
                                    const onClose = snackbar.options?.onClose;
                                    if (typeof onClose === 'function') {
                                        onClose();
                                    }
                                    toast.remove(snackbar.id);
                                }}
                                sx={{
                                    position: {
                                        xs: 'absolute',
                                        sm: 'static',
                                    },
                                    top: { xs: 4, sm: 'auto' },
                                    right: { xs: 4, sm: 'auto' },
                                    alignSelf: { xs: 'flex-start' },
                                    ml: { xs: 0, sm: 0.5 },
                                    flexShrink: 0,
                                    p: 0.5,
                                    zIndex: 1,
                                    pointerEvents: 'auto',
                                }}
                            >
                                <CloseIcon
                                    sx={{ fontSize: { xs: 18, sm: 14 } }}
                                />
                            </CustomIconButton>
                        ))}
                </Paper>
            </Grow>
        </Grid>
    );
};

export default IndividualToast;
